/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { defaultValue } from './AreaToolbarUndoRedo';

const AreaToolbarUndoRedoManager: ComponentManagerFn<'AreaToolbarUndoRedo'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AreaToolbarUndoRedo'>(
      platform,
      'AreaToolbarUndoRedo',
    ),
    type: 'AreaToolbarUndoRedo',
    name: 'Undo Redo',
    defaultValue,
    props: {},
    category: 'IoT',
    icon: { iconName: 'undo' },
    description: `**Overview**<br/>
The Area Toolbar Undo/Redo component is a utility control set for the **IoT Image Mapper**. It adds two navigation buttons—"Undo" and "Redo"—to the toolbar, giving users the ability to revert or re-apply their recent drawing actions.
Use this component to improve the user experience during complex map editing sessions, allowing users to correct mistakes (like accidentally moving a polygon or drawing a line incorrectly) without frustration.

**1. Functionality**<br/>
* **Undo:** Reverts the last action performed on the canvas (e.g., "Undo Move," "Undo Resize," or "Undo Create Zone").
* **Redo:** Re-applies an action that was just undone, allowing users to toggle between states.
* **State Management:** The component automatically tracks the history stack of user interactions within the current editing session. Once the "Save" button is clicked or the session is closed, the history stack is typically cleared.

**2. Configuration**<br/>
* **Zero Configuration:** This component is a "plug-and-play" utility with no configurable properties. It simply needs to be added as a child of the **Area Toolbar** to function.
* **Automatic Visibility:** The buttons automatically handle their own enabled/disabled states based on whether there are actions available in the history stack to undo or redo.

**3. Best Use Cases**<br/>
* **Precision Editing:** Essential for users drawing complex, multi-point polygons where a single misplaced vertex can distort the entire shape.
* **Safety Net:** Providing a quick way to recover from accidental deletions of zones or unintended movements of established boundaries.
* **Experimental Layouts:** Allowing users to quickly test different zone configurations and revert back if the new layout doesn't fit the requirements.`,
  };
};

export default AreaToolbarUndoRedoManager;
